/**
 * Logistics edit section for the venue page Details tab. Edits each structured
 * field's text while preserving its teacher-visibility flag.
 */
import type { VenueDetail, VenueLogistics, VenueLogisticsKey } from '../../../../api/venues';
import { Field } from '../venueCreateFields';

import { TeacherEye } from './detailBadges';
import { EditSection } from './EditSection';
import { ReadGrid, type ReadRow } from './ReadGrid';
import type { SectionProps } from './section-props';
import { Input, Textarea, useFormDraft } from './sectionHelpers';

const LOGISTICS: { key: VenueLogisticsKey; label: string; area: boolean; teacher: boolean }[] = [
  { key: 'parking', label: 'Parking info', area: true, teacher: true },
  { key: 'loadIn', label: 'Load-in notes', area: true, teacher: true },
  { key: 'powerAv', label: 'Power / AV', area: true, teacher: true },
  { key: 'space', label: 'Room / space details', area: true, teacher: true },
  { key: 'onDuty', label: 'On-duty contact note', area: true, teacher: true },
  { key: 'leadTime', label: 'Booking lead time', area: false, teacher: false },
  { key: 'weeknights', label: 'Available weeknights', area: false, teacher: false },
  { key: 'blackout', label: 'Blackout windows', area: false, teacher: false },
];

const teacherOf = (venue: VenueDetail, f: (typeof LOGISTICS)[number]) =>
  venue.logistics?.[f.key]?.teacherVisible ?? f.teacher;

export function LogisticsSection({ venue, readOnly, save }: SectionProps) {
  const seed = Object.fromEntries(
    LOGISTICS.map((f) => [f.key, venue.logistics?.[f.key]?.value ?? '']),
  ) as Record<VenueLogisticsKey, string>;
  const { draft, setField, reset } = useFormDraft(seed, venue.updatedAt);

  const onSave = () => {
    const next: VenueLogistics = {};
    for (const f of LOGISTICS) {
      const value = draft[f.key].trim();
      if (value) next[f.key] = { value, teacherVisible: teacherOf(venue, f) };
    }
    return save({ logistics: Object.keys(next).length > 0 ? next : null });
  };

  return (
    <EditSection
      title="Logistics"
      hint="teachers read the marked fields day-of"
      readOnly={readOnly}
      onCancel={reset}
      onSave={onSave}
      view={
        <ReadGrid
          cols={1}
          rows={LOGISTICS.map(
            (f): ReadRow => ({
              label: f.label,
              value: venue.logistics?.[f.key]?.value,
              teacher: teacherOf(venue, f),
              full: true,
            }),
          )}
        />
      }
      form={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {LOGISTICS.map((f) => (
            <Field key={f.key} label={f.label} hint={<TeacherEye on={teacherOf(venue, f)} />}>
              {f.area ? (
                <Textarea rows={1} value={draft[f.key]} onChange={(v) => setField(f.key, v)} />
              ) : (
                <Input
                  value={draft[f.key]}
                  onChange={(v) => setField(f.key, v)}
                  style={{ maxWidth: 320 }}
                />
              )}
            </Field>
          ))}
        </div>
      }
    />
  );
}
